import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const VERIFY_OTP_URL =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Auth/VerifyOtp";

const RESEND_OTP_URL =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Auth/ResendOtp";

function Otp() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const phone = state?.phone || "";
  const maskedPhone = useMemo(() => {
    if (!phone) return "";
    const p = String(phone);
    return `***${p.slice(-2)}`;
  }, [phone]);

  const [otp, setOtp] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [cooldown, setCooldown] = useState(0);

  const inputRefs = useRef([]);

  useEffect(() => {
    if (!phone) setError("Phone number missing. Please login/signup again.");
  }, [phone]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const otpValue = otp.join("");

  const focusIndex = (i) => {
    const el = inputRefs.current[i];
    if (el) el.focus();
  };

  const handleChange = (i, value) => {
    setError("");
    setSuccess("");

    const v = value.replace(/\D/g, "");

    if (v.length > 1) {
      const next = [...otp];
      for (let k = 0; k < v.length && i + k < 4; k++) next[i + k] = v[k];
      setOtp(next);
      focusIndex(Math.min(i + v.length, 3));
      return;
    }

    const next = [...otp];
    next[i] = v;
    setOtp(next);
    if (v && i < 3) focusIndex(i + 1);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      if (otp[i]) {
        const next = [...otp];
        next[i] = "";
        setOtp(next);
        return;
      }
      if (i > 0) {
        focusIndex(i - 1);
        const next = [...otp];
        next[i - 1] = "";
        setOtp(next);
      }
    }
    if (e.key === "ArrowLeft" && i > 0) focusIndex(i - 1);
    if (e.key === "ArrowRight" && i < 3) focusIndex(i + 1);
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData?.getData("text") || "").replace(/\D/g, "");
    if (!text) return;
    e.preventDefault();

    const next = ["", "", "", ""];
    for (let i = 0; i < 4; i++) next[i] = text[i] || "";
    setOtp(next);
    focusIndex(Math.min(text.length, 4) - 1);
  };

  const validate = () => {
    if (!phone) return "Phone number missing. Please login/signup again.";
    if (otpValue.length !== 4) return "Please enter the 4-digit OTP.";
    return "";
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const v = validate();
    if (v) return setError(v);

    setLoading(true);
    try {
      const res = await fetch(VERIFY_OTP_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: String(phone), otp: otpValue }),
      });

      const text = await res.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = { message: text };
      }

      if (!res.ok) {
        throw new Error(
          data?.message ||
            data?.error ||
            `Verify OTP failed (HTTP ${res.status})`,
        );
      }

      setSuccess(data?.message || "OTP verified successfully!");

      if (data?.token) {
        if (data?.role === "admin") {
          localStorage.setItem("SLNAdminToken", data.token);
          navigate("/manage/all-courses", { replace: true });
        } else {
          localStorage.setItem("SLNToken", data.token);
          navigate("/dashboard", { replace: true });
        }
        return;
      }

      navigate("/", { replace: true });
    } catch (err) {
      setError(err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Resend OTP using /Auth/ResendOtp
  const resendOtp = async () => {
    setError("");
    setSuccess("");

    if (!phone)
      return setError("Phone number missing. Please login/signup again.");
    if (cooldown > 0) return;

    setResending(true);
    try {
      const res = await fetch(RESEND_OTP_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: String(phone) }),
      });

      const text = await res.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = { message: text };
      }

      if (!res.ok) {
        throw new Error(
          data?.message ||
            data?.error ||
            `Resend OTP failed (HTTP ${res.status})`,
        );
      }

      setSuccess(data?.message || "OTP resent successfully!");
      setCooldown(30);

      setOtp(["", "", "", ""]);
      focusIndex(0);
    } catch (err) {
      setError(err?.message || "Could not resend OTP.");
    } finally {
      setResending(false);
    }
  };

  const isComplete = otpValue.length === 4;

  useEffect(() => {
    if (!phone) {
      navigate("/", { replace: true });
    }
  }, [phone, navigate]);

  return (
    <section className="min-vh-100 d-flex align-items-center bg-light">
      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-12 col-md-9 col-lg-6 col-xl-5">
            <div className="card border-0 shadow-sm rounded-5 overflow-hidden">
              <div className="card-body px-6 pt-6">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex align-items-center gap-3">
                    <img
                      src="/assets/images/logo.png"
                      alt="logo"
                      width="48"
                      height="48"
                      className="rounded-3"
                    />
                    <div>
                      <h4 className="mb-0 fw-semibold text-dark lh-1">
                        Verify OTP
                      </h4>
                      <p className="text-secondary fs-5 lh-sm m-0 mt-2">
                        Enter the code we sent to
                        <br />
                        <i className="bi bi-whatsapp text-success me-1"></i>{" "}
                        Whatsapp No.{" "}
                        <span className="fw-semibold text-dark">
                          &nbsp;
                          {maskedPhone || "your number"}
                        </span>
                      </p>
                    </div>
                  </div>

                  <span
                    onClick={() => navigate("/", { replace: true })}
                    className="btn btn-outline-secondary btn-sm px-3 rounded-pill"
                  >
                    <i className="bi bi-arrow-left me-1"></i> Back
                  </span>
                </div>

                {error ? (
                  <div className="alert alert-danger py-2 px-4 rounded-4">
                    {error}
                  </div>
                ) : null}
                {success ? (
                  <div className="alert alert-success py-2 px-4 rounded-4">
                    {success}
                  </div>
                ) : null}

                <form onSubmit={verifyOtp}>
                  <div className="mb-3">
                    <label className="form-label text-dark fw-semibold">
                      One Time Password
                    </label>

                    <div
                      className="col-lg-6 d-flex gap-1 justify-content-between"
                      onPaste={handlePaste}
                    >
                      {otp.map((digit, i) => (
                        <input
                          key={i}
                          ref={(el) => (inputRefs.current[i] = el)}
                          inputMode="numeric"
                          autoComplete={i === 0 ? "one-time-code" : "off"}
                          maxLength={1}
                          className="form-control text-center fs-3 fw-bold rounded-3"
                          style={{ height: 50 }}
                          value={digit}
                          onChange={(e) => handleChange(i, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(i, e)}
                          disabled={loading}
                          aria-label={`OTP digit ${i + 1}`}
                        />
                      ))}
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <small className="text-secondary fs-5 ls-sm">
                        Didn’t receive the code?
                      </small>

                      <button
                        type="button"
                        className="btn btn-link p-0 fw-medium text-decoration-none ls-sm"
                        onClick={resendOtp}
                        disabled={resending || loading || cooldown > 0}
                      >
                        {cooldown > 0
                          ? `Resend in ${cooldown}s`
                          : resending
                            ? "Resending OTP"
                            : "Resend OTP"}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary w-100 rounded-pill py-2 fw-medium ls-sm"
                    disabled={!isComplete || loading}
                  >
                    {loading ? "Verifying OTP" : "Verify OTP"}
                  </button>
                </form>
              </div>

              <div className="bg-white border-top">
                <div className="px-4 py-3 d-flex justify-content-between align-items-center">
                  <small className="text-secondary fs-5">
                    Having trouble?{" "}
                    <Link to="/contact" className="text-decoration-none">
                      Contact Support
                    </Link>
                  </small>
                  <div className="d-flex gap-1">
                    <a
                      href="https://www.facebook.com/profile.php?id=61586179350665"
                      target="_blank"
                    >
                      <img src="/assets/images/facebook.png" width="30" />
                    </a>
                    <img src="/assets/images/instagram.png" width="30" />
                    <img src="/assets/images/telegram.png" width="30" />
                    <a href="https://x.com/AcademySln64983" target="_blank">
                      <img src="/assets/images/x.png" width="30" />
                    </a>
                    <a
                      href="https://www.youtube.com/@SLNAcademyOfficial"
                      target="_blank"
                    >
                      <img src="/assets/images/youtube.png" width="30" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Otp;
